import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";
import Loading from "../loadingScreen/Loading";
import ErrorModal from "../modals/errorModal/ErrorModal";
import PaymentContext from "../../../context/payment.context";
import { formatOtpTime } from "../../../Utils/utils";
import { getTransactionStatus, selectTransactionData } from "../../../slices/TransactionIdSlice";
import {
  resetPaymentInitiation,
  resetPaymentInitiationLoading,
  showFailedModal,
} from "../../../slices/PaymentInitiationSlice";
import { closeAutoRefundUnresolved, hideAutoRefundLoader } from "../../../slices/AutoRefundSlice";
import "./autoRefundLoader.css";

const STATUS_POLL_INTERVAL_MS = 4000;

const SUCCESS_STATUSES = ["PAID", "PROCESSING"];
const isSuccessStatus = (status) => SUCCESS_STATUSES.includes(String(status || "").toUpperCase());

const secondsUntil = (isoTime) => Math.max(0, Math.ceil((Date.parse(isoTime) - Date.now()) / 1000));

// Full-screen loader. It covers the checkout, so the shopper can not start a new QR or pick another payment method.
const LoaderScreen = ({ subText }) => {
  const { t } = useTranslation();
  const paymentContext = useContext(PaymentContext);

  // no Cancel button here, even if another flow asked for it
  return (
    <div className="auto-refund-loader">
      <PaymentContext.Provider value={{ ...paymentContext, showCancelBtn: false }}>
        <Loading text={t("AUTO_REFUND_VERIFYING_PAYMENT")} subText={subText} />
      </PaymentContext.Provider>
    </div>
  );
};

// The auto refund window is running. For its whole length it shows the loader with a countdown to
// autoRefundAt and keeps polling the payment status. A success ends it and the existing code shows the
// success screen. If autoRefundAt passes without a success, it shows the refund message.
const VerifyingPayment = ({ transactionId, autoRefundAt }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setShowCancelBtn, setShowErroModalForUpiIntent } = useContext(PaymentContext);
  const token = useSelector((state) => state.paymentSessionDetails.sessionDetails?.token);
  const transactionData = useSelector((state) => selectTransactionData(state));
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(autoRefundAt));
  const [showRefundScreen, setShowRefundScreen] = useState(false);
  const pollingRef = useRef(null);
  const countdownRef = useRef(null);

  const onClose = () => dispatch(hideAutoRefundLoader());

  // The shopper may start a fresh payment after this: a new QR or another payment method.
  // The pages that were waiting on the result (see unresolvedTransactionId) carry on with their failure handling.
  const onCloseRefundScreen = () => {
    dispatch(resetPaymentInitiation());
    dispatch(resetPaymentInitiationLoading());
    // a failed status in the window must not show the failed modal after the refund message
    dispatch(showFailedModal(false));
    dispatch(closeAutoRefundUnresolved());
  };

  const stopVerifying = () => {
    clearInterval(pollingRef.current);
    clearInterval(countdownRef.current);
  };

  const endWait = async () => {
    stopVerifying();
    // one last check, in case the success arrived in the last seconds
    const result = await dispatch(getTransactionStatus({ token, transactionId }));
    if (isSuccessStatus(result?.payload?.status)) {
      onClose();
    } else {
      setShowRefundScreen(true);
    }
  };

  useEffect(() => {
    // the UPI intent loading screen and its Cancel button give way to this loader
    setShowCancelBtn(false);
    setShowErroModalForUpiIntent(false);

    // keep polling the payment status as before
    pollingRef.current = setInterval(() => {
      dispatch(getTransactionStatus({ token, transactionId }));
    }, STATUS_POLL_INTERVAL_MS);

    // count down to the time the backend gave, never a fixed number of seconds
    const tick = () => {
      const left = secondsUntil(autoRefundAt);
      setSecondsLeft(left);
      if (left <= 0) endWait();
    };
    countdownRef.current = setInterval(tick, 1000);
    tick();

    return stopVerifying;
  }, []);

  // a success before autoRefundAt ends the wait
  useEffect(() => {
    if (showRefundScreen || !isSuccessStatus(transactionData?.status)) return;
    if (transactionData?.transactionId && transactionData.transactionId !== transactionId) return;
    stopVerifying();
    onClose();
  }, [transactionData]);

  if (showRefundScreen) {
    return (
      <ErrorModal
        icon={
          <InfoCircleOutlined style={{ color: "#FF4D4F", fontSize: "22px" }} />
        }
        title={t("PAYMENT_FAILED")}
        desc={t("AUTO_REFUND_MESSAGE")}
        showModal={true}
        primaryOnClick={onCloseRefundScreen}
        primaryBtnText={t("RETRY")}
        showSecondaryButton={false}
      />
    );
  }

  return <LoaderScreen subText={formatOtpTime(secondsLeft)} />;
};

// Mounted once for the whole checkout (see BoxPayCheckout). It shows when the timer api is called and
// the backend started the auto refund window (see startAutoRefund).
const AutoRefundLoader = () => {
  const transactionId = useSelector((state) => state.autoRefund.activeTransactionId);
  const autoRefundAt = useSelector((state) => state.autoRefund.autoRefundAt);

  if (!transactionId) return null;
  // the timer api is still being called: block the checkout, no countdown yet
  if (!autoRefundAt) return <LoaderScreen subText="" />;
  return <VerifyingPayment key={transactionId} transactionId={transactionId} autoRefundAt={autoRefundAt} />;
};

export default AutoRefundLoader;
